// src/components/Organizer/SettingsPage.tsx
import React, { useState } from "react";
import { toast } from "react-toastify";
import BankAccountSettings from "./BankAccountSettings";

type GeneralSettings = {
	defaultLanguage: string;
	timezone: string;
	dateFormat: string;
	weekStart: "monday" | "sunday";
	brandColor: string;
	autoReport: boolean;
};

type NotificationSettings = {
	eventReminders: boolean;
	salesAlerts: boolean;
	payoutNotices: boolean;
	weeklyDigest: boolean;
	emergencySms: boolean;
};

type SecuritySettings = {
	twoFactor: boolean;
	loginAlerts: boolean;
	sessionTimeout: number;
};

type IntegrationSettings = {
	webhookUrl: string;
	sandboxMode: boolean;
	apiKey: string;
};

const SectionCard: React.FC<{
	title: string;
	description?: string;
	children: React.ReactNode;
}> = ({ title, description, children }) => (
	<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-4">
		<div>
			<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
				{title}
			</h3>
			{description && (
				<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
					{description}
				</p>
			)}
		</div>
		{children}
	</div>
);

const OrganizerSettings: React.FC = () => {
	const [generalSettings, setGeneralSettings] = useState<GeneralSettings>({
		defaultLanguage: "ja",
		timezone: "Asia/Tokyo",
		dateFormat: "YYYY年M月D日 HH:mm",
		weekStart: "monday",
		brandColor: "#2563eb",
		autoReport: true,
	});
	const [notificationSettings, setNotificationSettings] =
		useState<NotificationSettings>({
			eventReminders: true,
			salesAlerts: true,
			payoutNotices: true,
			weeklyDigest: false,
			emergencySms: false,
		});
	const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
		twoFactor: false,
		loginAlerts: true,
		sessionTimeout: 30,
	});
	const [integrationSettings, setIntegrationSettings] =
		useState<IntegrationSettings>({
			webhookUrl: "https://example.com/webhook",
			sandboxMode: true,
			apiKey: "pk_live_1234-XXXX-5678",
		});
	const [savingGeneral, setSavingGeneral] = useState(false);
	const [savingNotifications, setSavingNotifications] = useState(false);
	const [savingSecurity, setSavingSecurity] = useState(false);
	const [savingIntegrations, setSavingIntegrations] = useState(false);

	const recentSessions = [
		{ device: "MacBook Pro", location: "東京", lastActive: "2025/11/15 22:14" },
		{
			device: "iPhone 15 Pro",
			location: "横浜",
			lastActive: "2025/11/14 08:03",
		},
	];

	const simulateSave = async (
		message: string,
		setter: (state: boolean) => void
	) => {
		setter(true);
		await new Promise((resolve) => setTimeout(resolve, 700));
		setter(false);
		toast.success(message);
	};

	const handleGeneralSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		await simulateSave("表示設定を保存しました", setSavingGeneral);
	};

	const handleNotificationSave = async () => {
		await simulateSave("通知設定を保存しました", setSavingNotifications);
	};

	const handleSecuritySave = async () => {
		await simulateSave("セキュリティ設定を保存しました", setSavingSecurity);
	};

	const handleIntegrationSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		await simulateSave(
			"インテグレーション設定を保存しました",
			setSavingIntegrations
		);
	};

	const handleApiKeyCopy = async () => {
		try {
			await navigator.clipboard.writeText(integrationSettings.apiKey);
			toast.success("APIキーをコピーしました");
		} catch (error) {
			toast.error("クリップボードへのコピーに失敗しました");
		}
	};

	const handleRegenerateKey = () => {
		const randomKey =
			"pk_live_" +
			Math.random().toString(36).substring(2, 6).toUpperCase() +
			"-" +
			Math.random().toString(36).substring(2, 10);
		setIntegrationSettings({ ...integrationSettings, apiKey: randomKey });
		toast.info("新しいAPIキーを生成しました。保存を忘れずに！");
	};

	return (
		<div className="container mx-auto max-w-5xl p-6 min-h-screen">
			<div className="mb-8">
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
					<div>
						<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
							設定
						</h2>
						<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
							ダッシュボードの表示、通知、セキュリティ、連携をカスタマイズします
						</p>
					</div>
					<button className="text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300">
						ヘルプを見る
					</button>
				</div>
			</div>

			<div className="space-y-6">
				<SectionCard
					title="表示・基本設定"
					description="イベントページやダッシュボードに共通で使われる基本ルールを設定します"
				>
					<form onSubmit={handleGeneralSubmit} className="space-y-5">
						<div className="grid md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									表示言語
								</label>
								<select
									value={generalSettings.defaultLanguage}
									onChange={(e) =>
										setGeneralSettings({
											...generalSettings,
											defaultLanguage: e.target.value,
										})
									}
									className="w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 px-3 py-2"
								>
									<option value="ja">日本語</option>
									<option value="en">英語</option>
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									タイムゾーン
								</label>
								<select
									value={generalSettings.timezone}
									onChange={(e) =>
										setGeneralSettings({
											...generalSettings,
											timezone: e.target.value,
										})
									}
									className="w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 px-3 py-2"
								>
									<option value="Asia/Tokyo">(UTC+09:00) 東京</option>
									<option value="Asia/Seoul">(UTC+09:00) ソウル</option>
									<option value="Asia/Bangkok">(UTC+07:00) バンコク</option>
									<option value="America/Los_Angeles">
										(UTC-08:00) ロサンゼルス
									</option>
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									日付フォーマット
								</label>
								<select
									value={generalSettings.dateFormat}
									onChange={(e) =>
										setGeneralSettings({
											...generalSettings,
											dateFormat: e.target.value,
										})
									}
									className="w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 px-3 py-2"
								>
									<option value="YYYY年M月D日 HH:mm">2025年3月7日 19:00</option>
									<option value="YYYY/MM/DD HH:mm">2025/03/07 19:00</option>
									<option value="MM/DD/YYYY h:mm A">03/07/2025 7:00 PM</option>
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									週の開始曜日
								</label>
								<div className="flex gap-3">
									<label className="flex items-center space-x-2 text-gray-900 dark:text-white">
										<input
											type="radio"
											name="weekStart"
											value="monday"
											checked={generalSettings.weekStart === "monday"}
											onChange={() =>
												setGeneralSettings({
													...generalSettings,
													weekStart: "monday",
												})
											}
											className="h-4 w-4 text-violet-600 focus:ring-violet-500 dark:focus:ring-violet-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
										/>
										<span>月曜日</span>
									</label>
									<label className="flex items-center space-x-2 text-gray-900 dark:text-white">
										<input
											type="radio"
											name="weekStart"
											value="sunday"
											checked={generalSettings.weekStart === "sunday"}
											onChange={() =>
												setGeneralSettings({
													...generalSettings,
													weekStart: "sunday",
												})
											}
											className="h-4 w-4 text-violet-600 focus:ring-violet-500 dark:focus:ring-violet-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
										/>
										<span>日曜日</span>
									</label>
								</div>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									ブランドカラー
								</label>
								<div className="flex items-center space-x-3">
									<input
										type="color"
										value={generalSettings.brandColor}
										onChange={(e) =>
											setGeneralSettings({
												...generalSettings,
												brandColor: e.target.value,
											})
										}
										className="h-10 w-16 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
									/>
									<span className="text-sm text-gray-600 dark:text-gray-400">
										ライブビューに即時反映されます
									</span>
								</div>
							</div>
						</div>
						<label className="flex items-center space-x-3">
							<input
								type="checkbox"
								checked={generalSettings.autoReport}
								onChange={(e) =>
									setGeneralSettings({
										...generalSettings,
										autoReport: e.target.checked,
									})
								}
								className="h-4 w-4 text-violet-600 focus:ring-violet-500 dark:focus:ring-violet-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded"
							/>
							<span className="text-sm text-gray-700 dark:text-gray-300">
								週次のパフォーマンスレポートを自動送信する
							</span>
						</label>
						<div className="flex justify-end">
							<button
								type="submit"
								disabled={savingGeneral}
								className={`bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-6 py-2 rounded-lg ${
									savingGeneral ? "opacity-60 cursor-not-allowed" : ""
								}`}
							>
								{savingGeneral ? "保存中..." : "保存する"}
							</button>
						</div>
					</form>
				</SectionCard>

				<SectionCard
					title="通知"
					description="重要なアラートやレポートを受け取る方法を設定します"
				>
					<div className="space-y-4">
						{(
							[
								{ key: "eventReminders", label: "イベント前リマインダー" },
								{ key: "salesAlerts", label: "チケット販売速報" },
								{ key: "payoutNotices", label: "入金ステータス通知" },
								{ key: "weeklyDigest", label: "週間ダイジェストメール" },
								{ key: "emergencySms", label: "緊急SMS連絡" },
							] as const
						).map((item) => (
							<div
								key={item.key}
								className="flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3"
							>
								<div>
									<p className="font-medium text-gray-900 dark:text-white">
										{item.label}
									</p>
									<p className="text-sm text-gray-500 dark:text-gray-400">
										{item.key === "emergencySms"
											? "障害発生時のみSMSで通知します"
											: "メールとアプリで同時に通知します"}
									</p>
								</div>
								<button
									type="button"
									role="switch"
									aria-checked={notificationSettings[item.key]}
									onClick={() =>
										setNotificationSettings({
											...notificationSettings,
											[item.key]: !notificationSettings[item.key],
										})
									}
									className={`relative inline-flex h-6 w-10 items-center rounded-full transition ${
										notificationSettings[item.key]
											? "bg-gradient-to-r from-violet-500 to-purple-600"
											: "bg-gray-300 dark:bg-gray-600"
									}`}
								>
									<span
										className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
											notificationSettings[item.key]
												? "translate-x-4"
												: "translate-x-1"
										}`}
									></span>
								</button>
							</div>
						))}
					</div>
					<div className="flex justify-end mt-4">
						<button
							onClick={handleNotificationSave}
							disabled={savingNotifications}
							className={`bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-6 py-2 rounded-lg ${
								savingNotifications ? "opacity-60 cursor-not-allowed" : ""
							}`}
						>
							{savingNotifications ? "保存中..." : "保存する"}
						</button>
					</div>
				</SectionCard>

				<SectionCard
					title="セキュリティ"
					description="ログインまわりの安全性や端末セッションを管理します"
				>
					<div className="space-y-5">
						<div className="flex items-center justify-between">
							<div>
								<p className="font-medium text-gray-900 dark:text-white">
									二要素認証
								</p>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									Authenticatorアプリでログインを保護します
								</p>
							</div>
							<label className="flex items-center space-x-2 text-gray-900 dark:text-white">
								<input
									type="checkbox"
									checked={securitySettings.twoFactor}
									onChange={(e) =>
										setSecuritySettings({
											...securitySettings,
											twoFactor: e.target.checked,
										})
									}
									className="h-4 w-4 text-violet-600 focus:ring-violet-500 dark:focus:ring-violet-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded"
								/>
								<span>{securitySettings.twoFactor ? "有効" : "無効"}</span>
							</label>
						</div>
						<div className="flex items-center justify-between">
							<div>
								<p className="font-medium text-gray-900 dark:text-white">
									ログインアラート
								</p>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									未知の端末からのアクセスをメールで通知します
								</p>
							</div>
							<label className="flex items-center space-x-2 text-gray-900 dark:text-white">
								<input
									type="checkbox"
									checked={securitySettings.loginAlerts}
									onChange={(e) =>
										setSecuritySettings({
											...securitySettings,
											loginAlerts: e.target.checked,
										})
									}
									className="h-4 w-4 text-violet-600 focus:ring-violet-500 dark:focus:ring-violet-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded"
								/>
								<span>
									{securitySettings.loginAlerts ? "受け取る" : "受け取らない"}
								</span>
							</label>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								自動ログアウトまでの時間 (分)
							</label>
							<input
								type="number"
								min={5}
								max={240}
								value={securitySettings.sessionTimeout}
								onChange={(e) =>
									setSecuritySettings({
										...securitySettings,
										sessionTimeout: Number(e.target.value),
									})
								}
								className="w-32 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 px-3 py-2"
							/>
						</div>
						<div>
							<p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
								最近のログインセッション
							</p>
							<div className="space-y-2">
								{recentSessions.map((session, index) => (
									<div
										key={`${session.device}-${index}`}
										className="flex items-center justify-between border border-dashed border-gray-300 dark:border-gray-600 rounded px-3 sm:px-4 py-2"
									>
										<span className="font-medium text-gray-900 dark:text-white">
											{session.device}
										</span>
										<span className="text-sm text-gray-500 dark:text-gray-400">
											{session.location}・{session.lastActive}
										</span>
									</div>
								))}
							</div>
						</div>
						<div className="flex flex-wrap gap-3 justify-end pt-4">
							<button
								type="button"
								onClick={() =>
									toast.info("他の端末からのログアウト処理を開始します")
								}
								className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 px-3 sm:px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600"
							>
								他の端末からログアウト
							</button>
							<button
								onClick={handleSecuritySave}
								disabled={savingSecurity}
								className={`bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-6 py-2 rounded-lg ${
									savingSecurity ? "opacity-60 cursor-not-allowed" : ""
								}`}
							>
								{savingSecurity ? "保存中..." : "保存する"}
							</button>
						</div>
					</div>
				</SectionCard>

				{/* 銀行口座設定 */}
				<BankAccountSettings />

				<SectionCard
					title="連携・API"
					description="Webhook や API キーを管理して、他サービスとの連携を強化します"
				>
					<form onSubmit={handleIntegrationSubmit} className="space-y-5">
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								Webhook URL
							</label>
							<input
								type="url"
								value={integrationSettings.webhookUrl}
								onChange={(e) =>
									setIntegrationSettings({
										...integrationSettings,
										webhookUrl: e.target.value,
									})
								}
								className="w-full border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 px-3 py-2"
								placeholder="https://"
								required
							/>
						</div>
						<label className="flex items-center space-x-3">
							<input
								type="checkbox"
								checked={integrationSettings.sandboxMode}
								onChange={(e) =>
									setIntegrationSettings({
										...integrationSettings,
										sandboxMode: e.target.checked,
									})
								}
								className="h-4 w-4 text-violet-600 focus:ring-violet-500 dark:focus:ring-violet-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded"
							/>
							<span className="text-sm text-gray-700 dark:text-gray-300">
								サンドボックス環境を有効にする（テスト送信にのみ使用）
							</span>
						</label>
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								APIキー
							</label>
							<div className="flex flex-col gap-3 md:flex-row md:items-center">
								<input
									type="text"
									readOnly
									value={integrationSettings.apiKey}
									className="flex-1 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg px-3 py-2"
								/>
								<div className="flex gap-2">
									<button
										type="button"
										onClick={handleApiKeyCopy}
										className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600"
									>
										コピー
									</button>
									<button
										type="button"
										onClick={handleRegenerateKey}
										className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600"
									>
										再発行
									</button>
								</div>
							</div>
						</div>
						<div className="flex justify-between flex-wrap gap-3">
							<button
								type="button"
								onClick={() =>
									toast.info("テストWebhookを送信しました (ダミー)")
								}
								className="text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300"
							>
								テスト送信
							</button>
							<button
								type="submit"
								disabled={savingIntegrations}
								className={`bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-6 py-2 rounded-lg ${
									savingIntegrations ? "opacity-60 cursor-not-allowed" : ""
								}`}
							>
								{savingIntegrations ? "保存中..." : "保存する"}
							</button>
						</div>
					</form>
				</SectionCard>
			</div>
		</div>
	);
};

export default OrganizerSettings;
